import { format, isSameDay } from "date-fns";

const LATE_RECORDING_THRESHOLD_MS = 10 * 60 * 1000;

/**
 * Formats a message instant time-first: `4:31 PM today` when the instant falls on the same day as
 * `now`, `4:31 PM on Aug 11, 2026` otherwise.
 */
export function formatMessageTime(date: Date, now: Date = new Date()): string {
  return format(date, isSameDay(date, now) ? "h:mm aa 'today'" : "h:mm aa 'on' MMM d, yyyy");
}

/**
 * Returns the skew-corrected time an offline-recorded message was actually recorded, or null when
 * the message should render as a normal (live) message.
 *
 * Offline mode queues driver recordings on-device and publishes them on reconnect. When the
 * recording-to-send offset (`clientSentAt` − `clientRecordedAt`) exceeds 10 minutes, the recording
 * time is worth surfacing so dispatchers don't read a stale message as current. The returned
 * instant is `serverReceivedAt` minus that offset, which cancels out device clock skew.
 *
 * @returns the corrected recording time, or null when any timestamp is missing/unparsable or the
 * offset is within the threshold.
 */
export function lateRecordedAt({
  clientRecordedAt,
  clientSentAt,
  serverReceivedAt,
}: {
  clientRecordedAt?: string | null;
  clientSentAt?: string | null;
  serverReceivedAt?: string | null;
}): Date | null {
  if (!clientRecordedAt || !clientSentAt || !serverReceivedAt) return null;

  const recordedMs = new Date(clientRecordedAt).getTime();
  const sentMs = new Date(clientSentAt).getTime();
  const receivedMs = new Date(serverReceivedAt).getTime();
  if (Number.isNaN(recordedMs) || Number.isNaN(sentMs) || Number.isNaN(receivedMs)) return null;

  const offsetMs = sentMs - recordedMs;
  if (offsetMs <= LATE_RECORDING_THRESHOLD_MS) return null;

  return new Date(receivedMs - offsetMs);
}
